import { TextField, useHasPermission } from "@dgs/core";
import { useQuery } from "@tanstack/react-query";
import { useField } from "formik";
import { FC, useEffect } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { tenantService } from "../../../admin/src/shared/api/tenantService";
import { userTenantService } from "../../api/userTenantService";
import { EventFormValues } from "../../types/event";
import { PermissionName } from "../../types/permission";

const PlannedPersonCountError = styled.div`
	${({ theme }) => theme.forms.error};
	margin-top: ${({ theme }) => theme.spacing(2)};
`;

const TenantLabel = styled.div`
	font-weight: ${({ theme }) => theme.fonts.weights.bold};
`;

const allTenantsQuery = {
	queryKey: tenantService.allOptionsKey,
	queryFn: () => tenantService.allOptions().then((r) => r.data.data),
};

const userTenantsQuery = {
	queryKey: userTenantService.allOptionsKey,
	queryFn: () => userTenantService.allOptions().then((r) => r.data.data),
};

export const EventTenantPlannedPersonCountField: FC = () => {
	const { t } = useTranslation();
	const [field, meta, helpers] = useField<EventFormValues["tenantPlannedPersonCounts"]>("tenantPlannedPersonCounts");

	const canReadTenants = useHasPermission<PermissionName>(["tenant.read"]);
	const { data: tenants = [] } = useQuery({
		...(canReadTenants ? allTenantsQuery : userTenantsQuery),
	});

	useEffect(() => {
		if (!tenants.length) {
			return;
		}

		const missingTenants = tenants.filter((tenant) => !field.value.some((x) => x.tenant?.id === tenant.id));

		if (!missingTenants.length) {
			return;
		}

		helpers.setValue([
			...field.value,
			...missingTenants.map((tenant) => ({
				tenant,
				plannedPersonCount: 0,
			})),
		]);
	}, [field.value, helpers, tenants]);

	const showTenantLabel = tenants.length > 1;

	return (
		<>
			{field.value.map((entry, idx) => (
				<div key={entry.tenant?.id ?? idx}>
					{showTenantLabel && <TenantLabel>{entry.tenant?.name}</TenantLabel>}
					<TextField
						name={`tenantPlannedPersonCounts.${idx}.plannedPersonCount`}
						type="number"
						label={t("Planned person count")}
						min="0"
					/>
				</div>
			))}

			{meta.touched && meta.error && typeof meta.error === "string" && (
				<PlannedPersonCountError>{meta.error}</PlannedPersonCountError>
			)}
		</>
	);
};
